/**
 * 置顶 FAQ 管理组件
 * 支持拖拽排序、添加/取消置顶
 */

import React, { useState, useEffect } from 'react';
import {
  Card, Empty, Spin, message, Modal, Button, Tag, Typography, Alert, List, Checkbox,
} from 'antd';
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from '@hello-pangea/dnd';
import {
  MenuOutlined, StopOutlined, ReloadOutlined, PlusOutlined,
} from '@ant-design/icons';
import { faqApi } from '../../services/api';
import { PinnedFaqItem } from '../../services/types';
import { clsx } from 'clsx';

const { Text } = Typography;

export interface PinnedFaqManagementProps {
  /** FAQ 列表变更回调 */
  onChange?: () => void;
}

export const PinnedFaqManagement: React.FC<PinnedFaqManagementProps> = ({
  onChange,
}) => {
  const [pinnedFaqs, setPinnedFaqs] = useState<PinnedFaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [maxPinned, setMaxPinned] = useState(6);

  // 添加置顶 Modal 状态
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [unpinnedFaqs, setUnpinnedFaqs] = useState<PinnedFaqItem[]>([]);
  const [selectedFaqIds, setSelectedFaqIds] = useState<number[]>([]);
  const [loadingUnpinned, setLoadingUnpinned] = useState(false);

  // 加载置顶 FAQ 列表
  const fetchPinnedFaqs = async () => {
    setLoading(true);
    try {
      const res = await faqApi.getPinned();
      if (res.success && res.data) {
        setPinnedFaqs(res.data.items || []);
      }
    } catch {
      message.error('加载置顶 FAQ 失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载置顶配置
  const fetchConfig = async () => {
    try {
      const res = await faqApi.getPinnedConfig();
      if (res.success && res.data) {
        setMaxPinned(res.data.max_pinned_faqs || 6);
      }
    } catch {
      // 忽略错误，使用默认值
    }
  };

  useEffect(() => {
    fetchPinnedFaqs();
    fetchConfig();
  }, []);

  // 处理拖拽完成
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(pinnedFaqs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // 更新本地状态
    const newItems = items.map((item, index) => ({
      ...item,
      pin_order: index + 1,
    }));
    setPinnedFaqs(newItems);

    // 批量更新到后端
    setReordering(true);
    try {
      const pins = newItems.map((item) => ({ id: item.id, pin_order: item.pin_order }));
      const res = await faqApi.batchUpdatePinned(pins);
      if (res.success) {
        message.success('排序已更新');
        onChange?.();
      }
    } catch {
      message.error('更新排序失败');
      // 恢复原状态
      setPinnedFaqs(pinnedFaqs);
    } finally {
      setReordering(false);
    }
  };

  // 打开添加置顶 Modal
  const handleOpenAddModal = async () => {
    setLoadingUnpinned(true);
    setSelectedFaqIds([]);
    try {
      // 获取所有 FAQ（limit 最大 100）
      const res = await faqApi.list({ limit: 100 });
      if (res.success && res.data) {
        const allFaqs = res.data.items || [];
        // 过滤掉已置顶的 FAQ
        const pinnedIds = new Set(pinnedFaqs.map((p) => p.id));
        const unpinned = allFaqs.filter((f: PinnedFaqItem) => !pinnedIds.has(f.id));
        setUnpinnedFaqs(unpinned);
      }
    } catch {
      message.error('加载未置顶 FAQ 失败');
    } finally {
      setLoadingUnpinned(false);
    }
    setAddModalVisible(true);
  };

  // 处理选择 FAQ
  const handleSelectFaq = (faqId: number, checked: boolean) => {
    if (checked) {
      setSelectedFaqIds((prev) => [...prev, faqId]);
    } else {
      setSelectedFaqIds((prev) => prev.filter((id) => id !== faqId));
    }
  };

  // 确认添加置顶
  const handleAddPinned = async () => {
    if (selectedFaqIds.length === 0) {
      message.warning('请选择要置顶的 FAQ');
      return;
    }

    // 计算可用的最小序号（从 1 开始，0 表示不置顶）
    const usedOrders = new Set(pinnedFaqs.map((f) => f.pin_order));
    let nextOrder = 1;
    while (usedOrders.has(nextOrder)) {
      nextOrder++;
    }

    try {
      // 逐个置顶选择的 FAQ
      for (let i = 0; i < selectedFaqIds.length; i++) {
        const faqId = selectedFaqIds[i];
        const res = await faqApi.pin(faqId, nextOrder + i);
        if (!res.success) {
          message.error(res.message || '添加置顶失败');
          return;
        }
      }
      message.success(`已成功置顶 ${selectedFaqIds.length} 个 FAQ`);
      setAddModalVisible(false);
      await fetchPinnedFaqs();
      onChange?.();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '添加置顶失败');
    }
  };

  // 取消置顶
  const handleUnpin = async (id: number, question: string) => {
    Modal.confirm({
      title: '确认取消置顶',
      content: `确定要将「${question}」取消置顶吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await faqApi.unpin(id);
          if (res.success) {
            message.success('已取消置顶');
            await fetchPinnedFaqs();
            onChange?.();
          } else {
            message.error(res.message || '取消置顶失败');
          }
        } catch (err: any) {
          message.error(err?.response?.data?.message || '取消置顶失败');
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* 顶部提示 */}
      <Alert
        message="拖拽调整置顶顺序"
        description={`拖拽卡片可调整置顶顺序，最多支持 ${maxPinned} 个置顶 FAQ。取消置顶后，FAQ 将不再显示在快捷提问区域。`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 工具栏 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">
          当前已置顶 {pinnedFaqs.length}/{maxPinned} 个 FAQ
        </Text>
        <div style={{ display: 'flex', gap: 8 }}>
          {pinnedFaqs.length < maxPinned && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenAddModal}
            >
              添加置顶
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={fetchPinnedFaqs}>
            刷新
          </Button>
        </div>
      </div>

      {/* 置顶 FAQ 列表 */}
      {pinnedFaqs.length === 0 ? (
        <Empty
          description="暂无置顶 FAQ"
          style={{ padding: '40px 0' }}
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="pinned-faqs">
            {(provided: any) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {pinnedFaqs.map((item, index) => (
                  <Draggable key={item.id} draggableId={String(item.id)} index={index} isDragDisabled={reordering}>
                    {(provided: any, snapshot: any) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.8 : 1,
                        }}
                      >
                        <Card
                          size="small"
                          className={clsx(
                            'transition-all duration-200',
                            snapshot.isDragging && 'shadow-lg'
                          )}
                          style={{
                            cursor: 'move',
                            background: item.bg_color,
                            borderColor: snapshot.isDragging ? '#3b82f6' : '#e4e4e7',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            {/* 拖拽手柄 */}
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                color: '#a1a1aa',
                                cursor: 'grab',
                                flexShrink: 0,
                                paddingTop: 2,
                              }}
                            >
                              <MenuOutlined />
                            </div>

                            {/* 序号 */}
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                background: item.color,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {index + 1}
                            </div>

                            {/* 内容 */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <Text strong ellipsis style={{ color: item.color }}>
                                  {item.question}
                                </Text>
                                {item.category && (
                                  <Tag color={item.color} style={{ margin: 0, fontSize: 11 }}>
                                    {item.category}
                                  </Tag>
                                )}
                              </div>
                              <Text
                                type="secondary"
                                ellipsis
                                style={{ fontSize: 12 }}
                              >
                                {item.answer}
                              </Text>
                            </div>

                            {/* 取消置顶按钮 */}
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<StopOutlined />}
                              onClick={() => handleUnpin(item.id, item.question)}
                              style={{ flexShrink: 0 }}
                            >
                              取消置顶
                            </Button>
                          </div>
                        </Card>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* 添加更多提示 */}
      {pinnedFaqs.length < maxPinned && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary">
            还可以添加 {maxPinned - pinnedFaqs.length} 个置顶 FAQ
          </Text>
        </div>
      )}

      {/* 添加置顶 Modal */}
      <Modal
        title="添加置顶 FAQ"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={handleAddPinned}
        okText="确认置顶"
        okButtonProps={{ disabled: selectedFaqIds.length === 0 }}
        width={600}
        destroyOnClose
      >
        <Spin spinning={loadingUnpinned}>
          {unpinnedFaqs.length === 0 ? (
            <Empty description="暂无可置顶的 FAQ" style={{ padding: '20px 0' }} />
          ) : (
            <>
              <Alert
                message="选择要置顶的 FAQ"
                description={`已选择 ${selectedFaqIds.length} 个 FAQ，添加后将自动分配序号`
                  + `（当前可用最小序号: ${pinnedFaqs.length}）`
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <List
                  dataSource={unpinnedFaqs}
                  renderItem={(item) => (
                    <List.Item
                      style={{ padding: '8px 0' }}
                      actions={[
                        <Checkbox
                          key="checkbox"
                          checked={selectedFaqIds.includes(item.id)}
                          onChange={(e) => handleSelectFaq(item.id, e.target.checked)}
                        />,
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text strong ellipsis style={{ maxWidth: 400 }}>
                              {item.question}
                            </Text>
                            {item.category && (
                              <Tag color={item.color} style={{ margin: 0, fontSize: 11 }}>
                                {item.category}
                              </Tag>
                            )}
                          </div>
                        }
                        description={
                          <Text type="secondary" ellipsis>
                            {item.answer}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default PinnedFaqManagement;
